<template>
  <Teleport to="body">
    <cl-modal
      :size="'md'"
      :test-id="props.testId"
      :visible="props.visible"
      @on-close="onClose"
    >
      <template #[`header.title`]>
        <cl-heading :size="'h4'">{{ t("modal-title-add-admin") }}</cl-heading>
      </template>
      <div>
        <section class="tw-mt-6 tw-mb-10">
          <cl-stepper :steps="steps" />
        </section>

        <!-- Add Administrator step -->
        <section v-if="adminProfileStep.current">
          <form autocomplete="off" @submit.prevent="submit">
            <div class="tw-mx-3 tw-mb-3 tw-grid tw-grid-cols-2 tw-gap-5">
              <div class="w-full w-2/4 px-2 mb-2 md:mb-0 tw-col-span-1">
                <cl-form-group>
                  <cl-form-label label-for="full-name">
                    {{ t("field-label-full-name") }}
                  </cl-form-label>
                  <cl-form-input
                    v-model:value="v$.createAdminForm.name.$model"
                    :name="'full-name'"
                    :type="'text'"
                    :placeholder="t('placeholder-full-name')"
                    :data-test-id="`${props.testId}-input-full-name`"
                    @blur="handleBlur('createAdminForm', 'name')"
                    @input="v$.createAdminForm.name.$reset()"
                    :state="
                      !v$.createAdminForm.name.$dirty
                        ? null
                        : !v$.createAdminForm.name.$error
                    "
                  />
                  <span
                    v-if="!v$.createAdminForm.name.$error"
                    class="tw-text-sm"
                  >
                    {{ t("hint-admin-name") }}
                  </span>
                  <span
                    v-if="v$.createAdminForm.name.$error"
                    class="tw-text-danger-500 tw-text-sm"
                  >
                    <span v-if="!v$.createAdminForm.name.required.$response">
                      {{ t("validation-text-admin-name") }}
                    </span>
                    <span
                      v-else-if="
                        !v$.createAdminForm.name.maxLength.$response ||
                        !v$.createAdminForm.name.minLength.$response
                      "
                    >
                      {{ t("validation-text-customer-name") }}
                    </span>
                  </span>
                </cl-form-group>
              </div>
              <div class="w-full md:w-1/2 px-2">
                <cl-form-group>
                  <cl-form-label label-for="email">
                    {{ t("field-label-email") }}
                  </cl-form-label>
                  <cl-form-input
                    v-model:value="v$.createAdminForm.email.$model"
                    :name="'email'"
                    :type="'text'"
                    :data-test-id="`${props.testId}-input-email`"
                    :placeholder="t('placeholder-text-email')"
                    :state="
                      !v$.createAdminForm.email.$dirty
                        ? null
                        : !v$.createAdminForm.email.$error
                    "
                    @blur="
                      () => {
                        handleBlur('createAdminForm', 'email');
                        isEmailTaken = false;
                        isEmailRecordWrong = false;
                      }
                    "
                    @input="
                      () => {
                        v$.createAdminForm.email.$reset();
                        isEmailTaken = false;
                        isEmailRecordWrong = false;
                      }
                    "
                  />
                  <span
                    v-if="
                      !v$.createAdminForm.email.$error &&
                      !isEmailTaken &&
                      !isEmailRecordWrong
                    "
                    class="tw-text-sm"
                  >
                    {{ t("hint-email") }}
                  </span>
                  <span
                    v-if="isEmailTaken"
                    class="tw-text-danger-500 tw-text-sm"
                  >
                    {{ t("validation-text-email-in-use") }}
                  </span>
                  <span
                    v-if="isEmailRecordWrong"
                    class="tw-text-danger-500 tw-text-sm"
                  >
                    {{ t("validation-text-valid-email-format") }}
                  </span>
                  <span
                    v-if="
                      v$.createAdminForm.email.$error &&
                      !isEmailTaken &&
                      !isEmailRecordWrong
                    "
                    class="tw-text-danger-500 tw-text-sm"
                  >
                    <span v-if="!v$.createAdminForm.email.required.$response">
                      {{ t("validation-text-required-field") }}
                    </span>
                    <span v-else-if="!v$.createAdminForm.email.email.$response">
                      {{ t("validation-text-valid-email-format") }}
                    </span>
                  </span>
                </cl-form-group>
              </div>
            </div>
            <div class="tw-mx-3 tw-mb-3 tw-grid tw-grid-cols-2 tw-gap-5">
              <div class="w-full w-2/4 px-2 mb-2 md:mb-0 tw-col-span-1">
                <cl-form-group>
                  <cl-form-label label-for="password">
                    {{ t("field-label-password") }}
                  </cl-form-label>
                  <t-password-input
                    v-model:value="v$.createAdminForm.password.$model"
                    :name="'password'"
                    :placeholder="t('placeholder-text-password')"
                    :data-test-id="`${props.testId}-input-password`"
                    :state="
                      !v$.createAdminForm.password.$dirty
                        ? undefined
                        : !v$.createAdminForm.password.$error
                    "
                    @blur="handleBlur('createAdminForm', 'password')"
                    @input="v$.createAdminForm.password.$reset()"
                  />
                  <span
                    v-if="!v$.createAdminForm.password.$error"
                    class="tw-text-sm"
                  >
                    {{ t("hint-password") }}
                  </span>
                  <span
                    v-if="v$.createAdminForm.password.$error"
                    class="tw-text-danger-500 tw-text-sm"
                  >
                    <span
                      v-if="!v$.createAdminForm.password.required.$response"
                    >
                      {{ t("validation-text-required-field") }}
                    </span>
                    <span
                      v-else-if="
                        !v$.createAdminForm.password.strongPassword.$response
                      "
                    >
                      {{ t("validation-text-password") }}
                    </span>
                  </span>
                </cl-form-group>
              </div>
              <div class="w-full md:w-1/2 px-2">
                <cl-form-group>
                  <cl-form-label label-for="confirm_password">
                    {{ t("field-label-confirm-password") }}
                  </cl-form-label>
                  <t-password-input
                    :id="'confirm_password'"
                    v-model:value="v$.createAdminForm.confirm_password.$model"
                    :name="'confirm_password'"
                    :data-test-id="`${props.testId}-input-confirm-password`"
                    :placeholder="t('placeholder-text-confirm-password')"
                    @blur="handleBlur('createAdminForm', 'confirm_password')"
                    :state="
                      !v$.createAdminForm.confirm_password.$dirty
                        ? null
                        : !v$.createAdminForm.confirm_password.$error
                    "
                  />
                  <span
                    v-if="v$.createAdminForm.confirm_password.$error"
                    class="tw-text-danger-500 tw-text-sm"
                  >
                    <span
                      v-if="
                        !v$.createAdminForm.confirm_password.required.$response
                      "
                    >
                      {{ t("validation-text-required-field") }}
                    </span>
                    <span
                      v-else-if="
                        !v$.createAdminForm.confirm_password.sameAsPassword
                          .$response
                      "
                    >
                      {{ t("validation-text-confirm-password") }}
                    </span>
                  </span>
                </cl-form-group>
              </div>
            </div>
          </form>
        </section>

        <!-- Administrator Roles step -->
        <section
          v-if="adminRolesStep.current"
          :data-test-id="`${props.testId}-stepper-section-edit-role`"
        >
          <div class="tw-flex tw-flex-col tw-gap-3 tw-pb-10">
            <label
              class="tw-pb-2"
              v-dompurify-html="
                t('text-block-select-admin-role', {
                  EmailAddress: v$.createAdminForm.email.$model,
                })
              "
            />

            <label
              v-for="role in ADMINISTRATOR_ROLES"
              :key="role.apiSlug"
              class="tw-flex tw-items-center tw-gap-2 tw-text-sm"
            >
              <cl-input-checkbox
                :id="role.apiSlug"
                :indeterminate="false"
                :checked="
                  v$.createAdminForm.roles.$model.includes(role.apiSlug)
                "
                :name="role.apiSlug"
                :class="'tw-rounded-full'"
                type="radio"
                @change="selectRole(role.apiSlug)"
                :data-test-id="`${props.testId}-role-select-${role.apiSlug}`"
              />
              {{ t(role.nameLabel) }}
            </label>
          </div>
        </section>
      </div>

      <template #[`footer.decline`]>
        <cl-button
          :variant="'secondary-ghost'"
          @on-click="onClose"
          :data-test-id="`${props.testId}-button-cancel`"
        >
          <span>{{ t("button-cancel") }}</span>
        </cl-button>
        <template v-if="adminRolesStep.current">
          <cl-button
            :variant="'secondary-outline'"
            :data-test-id="`${props.testId}-button-back-to-edit-user`"
            @on-click="goToStep(1)"
          >
            <span>{{ t("button-back") }}</span>
          </cl-button>
        </template>
      </template>
      <template #[`footer.accept`]>
        <template v-if="adminProfileStep.current">
          <cl-button
            variant="secondary"
            :data-test-id="`${props.testId}-button-next-to-edit-roles`"
            :disabled="!canProceed"
            @on-click="
              () => {
                adminProfileStep.completed = true;
                goToStep(2);
              }
            "
          >
            <span>{{ t("button-next") }}</span>
          </cl-button>
        </template>
        <template v-if="adminRolesStep.current">
          <div>
            <cl-button
              v-if="saving"
              :variant="'secondary'"
              disabled
              :data-test-id="`${props.testId}-button-saving`"
            >
              <cl-spinner :size="'small'" class="mr-2" />
              <span>{{ t("button-saving") }}</span>
            </cl-button>
            <cl-button
              v-else
              variant="secondary"
              :data-test-id="`${props.testId}-button-save`"
              @on-click="submit"
            >
              {{ t("button-save-changes") }}
            </cl-button>
          </div>
        </template>
      </template>
    </cl-modal>
  </Teleport>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { computed, onMounted, reactive, ref } from "vue";
import type { Step } from "@titanhq/frontend-component-library/dist/types/src/components/stepper/types";
import { useStore } from "vuex";
import {
  email,
  maxLength,
  minLength,
  required,
  sameAs,
} from "@vuelidate/validators";
import { useVuelidate } from "@vuelidate/core";
import { STRONG_PASSWORD } from "@/constants";
import TPasswordInput from "@/components/TPasswordInput";

import { useToastStore } from "@/components/TToasts/toast.store.ts";

const toastStore = useToastStore();

import { isAxiosError } from "axios";

interface AddAdministratorProps {
  visible: boolean;
  testId: string;
  parentUuid: string;
}
const props = defineProps<AddAdministratorProps>();

const emit = defineEmits<{
  (event: "on-close"): void;
  (event: "on-create"): void;
}>();

const { t } = useI18n();
const store = useStore();
const saving = ref<boolean>(false);
const isEmailTaken = ref<boolean>(false);
const isEmailRecordWrong = ref<boolean>(false);

// TODO - @Rob once p-1895 released, replace ADMINISTRATOR_ROLES with the global one:
const ADMINISTRATOR_ROLES = [
  {
    nameLabel: "role-super-administrator",
    apiSlug: "super-admin",
  },
  {
    nameLabel: "role-administrator",
    apiSlug: "admin",
  },
  {
    nameLabel: "role-read-only",
    apiSlug: "admin-ro",
  },
];

const adminProfileStep = ref<Step>({
  index: 1,
  label: t("stepper-step-add-admin"),
  current: true,
  completed: false,
  actionable: false,
});

const adminRolesStep = ref<Step>({
  index: 2,
  label: t("stepper-step-select-role"),
  current: false,
  completed: false,
  actionable: false,
});

const steps = computed<Step[]>(() => [
  adminProfileStep.value,
  adminRolesStep.value,
]);
const stepsRefs = [adminProfileStep, adminRolesStep];

const goToStep = (index: number | string) => {
  for (const step of stepsRefs) {
    if (index === step.value.index) {
      step.value.current = true;
      step.value.completed = false;
      continue;
    }
    step.value.current = false;
  }
};

const getInitialFormState = () => ({
  name: "",
  email: "",
  password: "",
  confirm_password: "",
  roles: ["super-admin"],
});

const form = reactive({
  createAdminForm: getInitialFormState(),
});

const rules = {
  createAdminForm: {
    name: { required, minLength: minLength(3), maxLength: maxLength(100) },
    email: { required, email },
    password: {
      required,
      strongPassword: (value: string) => STRONG_PASSWORD.test(value),
    },
    confirm_password: {
      required,
      sameAsPassword: sameAs(
        computed(() => form.createAdminForm.password),
        t("validation-text-confirm-password"),
      ),
    },
    roles: { required },
  },
};

const v$ = useVuelidate(rules, form);

function selectRole(selectedRole: string) {
  v$.value.createAdminForm.roles.$model = [selectedRole];
}
const submit = async () => {
  const isValid = await v$.value.$validate();
  if (!isValid) return;

  try {
    saving.value = true;

    await store.dispatch("users/createUser", {
      uuid: props.parentUuid,
      formData: form.createAdminForm,
    });

    toastStore.displayToast({
      title: t("toast-success"),
      message: t("toast-text-admin-added"),
      duration: 5000,
      variant: "success",
      testId: "add-administrator-toast-success",
    });
    emit("on-create");

    onClose();
  } catch (error: unknown) {
    if (isAxiosError(error) && error.response?.status === 422) {
      const errorMessage = error.response.data.fields[0].errors[0];
      if (errorMessage === "The email has already been taken.") {
        isEmailTaken.value = true;
        form.createAdminForm.email = "";
        v$.value.createAdminForm.email.$touch();
      } else if (errorMessage === "The email must be a valid email address.") {
        isEmailRecordWrong.value = true;
        form.createAdminForm.email = "";
        v$.value.createAdminForm.email.$touch();
      }
      goToStep(1);
    }
  } finally {
    saving.value = false;
  }
};

const canProceed = computed(() =>
  Object.entries(v$.value.createAdminForm)
    .filter(([key]) => key !== "roles")
    .every(([, field]) => !field.$error && field.$model?.trim?.() !== ""),
);

const onClose = () => {
  resetForm();
  resetSteps();
  emit("on-close");
};

const handleBlur = async (form: string, field: string) => {
  const isValid = await v$.value[form][field].$validate();
  if (!isValid) {
    return;
  }
};

const resetForm = () => {
  Object.assign(form.createAdminForm, getInitialFormState());
  v$.value.$reset();
  isEmailTaken.value = false;
  isEmailRecordWrong.value = false;
};

const resetSteps = () => {
  for (const step of stepsRefs) {
    step.value.current = false;
    step.value.completed = false;
  }
  adminProfileStep.value.current = true;
};

onMounted(() => {
  // By default, we pre-select "admin" role when no role is available:
  // TODO @Rob remove later on when other role checks available..
  if (form.createAdminForm.roles.length === 0) {
    form.createAdminForm.roles.push("admin");
  }
});
</script>
